<template>
  <div class="verificador-conflitos">
    <!-- Loading indicator durante verificação -->
    <v-progress-linear v-if="verificando" indeterminate color="primary" class="mt-2" />

    <!-- Mensagem de conflito -->
    <v-alert
      v-if="temConflitos && !verificando"
      type="error"
      border="left"
      class="mt-4"
      dense
      @click="mostrarConflitos = true"
    >
      <strong>Conflito detectado!</strong>
      {{ mensagemConflito }} <v-icon size="18">mdi mdi-cursor-default-click-outline</v-icon>
    </v-alert>

    <!-- Mensagem de sucesso -->
    <v-alert
      v-if="!temConflitos && verificacaoRealizada && !verificando"
      type="success"
      border="left"
      class="mt-4"
      dense
    >
      <strong>Sem conflitos!</strong> Não há aulas conflitadas neste horário.
    </v-alert>

    <!-- Bottom-sheet com aulas conflitadas -->
    <v-bottom-sheet v-model="mostrarConflitos" scrollable>
      <v-sheet class="text-center pa-md-8 mx-lg-auto" height="auto">
        <v-btn color="error" dark small @click="mostrarConflitos = false"> Fechar </v-btn>

        <h2 class="mt-4">Aulas Conflitadas</h2>
        <p class="mt-2 text-subtitle-2 grey--text">
          {{ aulasConflitadas.length }} aula(s) encontrada(s) neste horário
        </p>

        <v-simple-table class="mt-4" style="border: 1px solid #e0e0e0">
          <template>
            <thead style="background-color: #e0e0e0">
              <tr>
                <th class="text-left">#</th>
                <th class="text-left">Professor</th>
                <th class="text-left">Situação</th>
                <th class="text-left">Data/Horário</th>
                <th class="text-left">Turma</th>
                <th class="text-left">Disciplina</th>
              </tr>
            </thead>
            <tbody class="text-left">
              <tr v-for="item in aulasConflitadas" :key="item.id">
                <td>{{ item.id }}</td>
                <td>
                  {{ item.professor.nome }}
                  <br />
                  <v-chip color="grey" dark x-small>
                    {{ item.turma?.franquia?.descricao }}
                  </v-chip>
                </td>
                <td>{{ item.situacao ? item.situacao : "Aguardando" }}</td>
                <td>
                  <span>
                    {{ formatarData(item.data) }}
                  </span>
                  <v-chip
                    v-for="horario in item.horarios_com_descricao"
                    :key="horario.id"
                    color="grey"
                    dark
                    x-small
                    class="mx-1"
                  >
                    {{ horario.descricao }}
                  </v-chip>
                </td>
                <td>{{ item.turma?.descricao }}</td>
                <td>{{ item.disciplina?.descricao }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-sheet>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "EVerificadorConflitosAula",
  props: {
    data: {
      type: String,
      default: null,
    },
    horarioId: {
      type: Number,
      default: null,
    },
    turmaId: {
      type: Number,
      default: null,
    },
    aulaId: {
      type: Number,
      default: null,
    },
    e_aula_infantil: {
      type: Boolean,
      default: false,
    },
    verificarAutomaticamente: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    mostrarBottomSheetAutomaticamente: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      verificando: false,
      verificacaoRealizada: false,
      aulasConflitadas: [],
      mostrarConflitos: false,
    };
  },
  computed: {
    temConflitos() {
      return this.verificando || this.aulasConflitadas.length > 0;
    },
    mensagemConflito() {
      const qtd = this.aulasConflitadas.length;
      if (qtd === 1) {
        return "Já existe 1 aula neste dia e horário. Clique para ver detalhes.";
      }
      return `Já existem ${qtd} aulas neste dia e horário. Clique para ver detalhes.`;
    },
    parametros() {
      return {
        data: this.data,
        horarioId: this.horarioId,
        turmaId: this.turmaId,
      };
    },
  },
  watch: {
    parametros: {
      handler() {
        if (this.verificarAutomaticamente && !this.disabled) {
          this.verificarConflitos();
        }
      },
      deep: true,
    },
    disabled(newVal) {
      if (!newVal && this.verificarAutomaticamente) {
        this.verificarConflitos();
      }
    },
    temConflitos(newVal) {
      this.$emit("conflitos-encontrados", newVal);
    },
    aulasConflitadas(newVal) {
      if (newVal.length > 0 && this.mostrarBottomSheetAutomaticamente) {
        this.mostrarConflitos = true;
      }
    },
  },
  methods: {
    todosParametrosPreenchidos() {
      return this.data && this.horarioId && this.turmaId;
    },
    async verificarConflitos() {
      if (!this.todosParametrosPreenchidos()) {
        this.resetarEstado();
        return;
      }

      if (this.disabled) {
        return;
      }

      try {
        this.verificando = true;
        this.verificacaoRealizada = false;

        // Formata a data para o formato esperado pela API (DD/MM/YYYY)
        const dataFormatada = this.formatarDataParaAPI(this.data);

        const response = await this.$services.aulasService.pesquisarAulasEmConflitos(
          dataFormatada,
          this.horarioId,
          this.turmaId,
          this.aulaId,
          this.e_aula_infantil
        );

        this.aulasConflitadas = response.data || [];
        this.verificacaoRealizada = true;

        // Emite evento com os dados completos dos conflitos
        this.$emit("conflitos-carregados", this.aulasConflitadas);
      } catch (error) {
        this.$handleError(error);
        this.resetarEstado();
      } finally {
        this.verificando = false;
      }
    },
    formatarDataParaAPI(data) {
      if (!data) return null;

      // Se já está no formato DD/MM/YYYY, retorna
      if (data.includes("/")) {
        return data;
      }

      // Se está no formato YYYY-MM-DD, converte para DD/MM/YYYY
      if (data.includes("-")) {
        return data.split("-").reverse().join("/");
      }

      return data;
    },
    formatarData(data) {
      if (!data) return "";

      // Se já está no formato DD/MM/YYYY
      if (data.includes("/")) {
        return data;
      }

      // Se está no formato YYYY-MM-DD, converte para DD/MM/YYYY
      if (data.includes("-")) {
        return data.split("-").reverse().join("/");
      }

      return data;
    },
    resetarEstado() {
      this.aulasConflitadas = [];
      this.verificacaoRealizada = false;
      this.mostrarConflitos = false;
      this.$emit("conflitos-encontrados", false);
      this.$emit("conflitos-carregados", []);
    },
    // Método público para forçar verificação manual
    verificar() {
      this.verificarConflitos();
    },
  },
  beforeDestroy() {
    // Limpa o timer ao destruir o componente
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
};
</script>

<style scoped>
.verificador-conflitos {
  width: 100%;
}

.v-alert {
  cursor: pointer;
}

.v-alert:hover {
  opacity: 0.9;
}
</style>
